import * as React from 'react';

import { trackDesktopHintShow } from './tracking';
import { useApplicationContext } from '../../../../../../shared/common/containers/ApplicationContext';
import { LOCAL_STORAGE_IS_DESKTOP_FILTERS_WERE_APPLIED } from '../../../../../../shared/common/hooks/useUxFbPoll/constants';
import {
  getLocalStorageFiltersWereAppliedTwoTimes,
  setLocalStorageFiltersApplied,
} from '../../../../../../shared/common/hooks/useUxFbPoll/localStorageUtils';
import { usePriceCorrection } from '../../../../../../shared/hooks/usePriceCorrection';
import { FDealType, getRangeValue } from '../../../../../JsonQuery';
import { IJsonQuery } from '../../../../../api-models/common/json_query';

interface IUsePriceFilterHandlersParams {
  jsonQuery: IJsonQuery;
  dealType: FDealType;
  onChange: (action: { action: string; arguments: unknown[] }) => void;
  onApply: () => void;
}

interface IUsePriceFilterHandlersResult {
  showCorrectionHint: boolean;
  handlePriceMinChange: (gte: number | null) => void;
  handlePriceMaxChange: (lte: number | null) => void;
}

/**
 * Хук для обработки изменений цены с коррекцией для посуточной аренды.
 * Инкапсулирует логику:
 * - Коррекции цен при слишком маленьком разбеге
 * - Показа подсказки о коррекции
 * - Трекинга события показа подсказки
 * - Сохранения в localStorage для опроса UX
 */
export function usePriceFilterHandlers({
  jsonQuery,
  dealType,
  onChange,
  onApply,
}: IUsePriceFilterHandlersParams): IUsePriceFilterHandlersResult {
  const { config } = useApplicationContext();

  const price = getRangeValue('price')(jsonQuery);
  const { gte: priceMin = null, lte: priceMax = null } = price || {};

  // Состояние для показа hint (сбрасывается при unmount)
  const [showCorrectionHint, setShowCorrectionHint] = React.useState(false);

  // Получаем минимальный разбег между ценами из конфига (по умолчанию 1000₽)
  const pricesMinGap = config.get<number>('Dailyrent.PriceInputs.MinGap') || 1000;

  // Хук для расчёта скорректированных цен
  const calculateCorrectedPrice = usePriceCorrection({ pricesMinGap });

  const isLocalStorageFiltersWereAppliedTwoTimes = getLocalStorageFiltersWereAppliedTwoTimes(
    LOCAL_STORAGE_IS_DESKTOP_FILTERS_WERE_APPLIED,
  );

  const saveFilterApplied = React.useCallback(() => {
    if (!isLocalStorageFiltersWereAppliedTwoTimes) {
      setLocalStorageFiltersApplied(LOCAL_STORAGE_IS_DESKTOP_FILTERS_WERE_APPLIED);
    }
  }, [isLocalStorageFiltersWereAppliedTwoTimes]);

  const handlePriceMinChange = React.useCallback(
    (gte: number | null) => {
      const { adjustedMin, adjustedMax, wasCorrected } = calculateCorrectedPrice({
        newMin: gte,
        newMax: priceMax,
        isMinChanged: true,
      });

      if (wasCorrected) {
        setShowCorrectionHint(true);
        trackDesktopHintShow();
        // Если скорректированный минимум равен 0, передаём null для отображения пустого поля
        const minValue = adjustedMin === 0 ? null : adjustedMin;
        onChange({ action: 'setPrice', arguments: [minValue, adjustedMax] });
        onApply();
        saveFilterApplied();

        return;
      }

      onChange({ action: 'setPriceMin', arguments: [gte] });
      onApply();
      saveFilterApplied();
    },
    [calculateCorrectedPrice, priceMax, onChange, onApply, saveFilterApplied],
  );

  const handlePriceMaxChange = React.useCallback(
    (lte: number | null) => {
      // Коррекция только для посуточной аренды
      const { adjustedMin, adjustedMax, wasCorrected } = calculateCorrectedPrice({
        newMin: priceMin,
        newMax: lte,
        isMinChanged: false,
      });

      if (wasCorrected) {
        setShowCorrectionHint(true);
        trackDesktopHintShow();
        // Если скорректированный минимум равен 0, передаём null для отображения пустого поля
        const minValue = adjustedMin === 0 ? null : adjustedMin;
        onChange({ action: 'setPrice', arguments: [minValue, adjustedMax] });
        onApply();
        saveFilterApplied();

        return;
      }

      onChange({ action: 'setPriceMax', arguments: [lte] });
      onApply();
      saveFilterApplied();
    },
    [calculateCorrectedPrice, priceMin, onChange, onApply, saveFilterApplied],
  );

  return {
    showCorrectionHint,
    handlePriceMinChange,
    handlePriceMaxChange,
  };
}
